import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { motion, useReducedMotion, type Transition } from "framer-motion";
import { Loader2 } from "lucide-react";

import InfiniteScroll from "@/modern/components/infinite-scroll";
import { useMediaQuery } from "@/modern/hooks/useMediaQuery";
import { usePostMountFlush } from "@/modern/hooks/usePostMountFlush";
import { cn } from "@/modern/utils";

import type { Notification, NotificationConfigContext } from "../types";
import {
  MOSAIC_WIDE_QUERY,
  mosaicGridHeight,
  mosaicPlacementStyle,
  mosaicRowUnitPx,
  placeMosaicLayouts,
} from "../utils/mosaicPlacement";
import {
  NotificationTile,
  type NotificationMosaicLayout,
} from "./NotificationTile";

const MOSAIC_COLUMNS = 3;

const MOSAIC_GRID_CLS =
  "grid grid-cols-3 auto-rows-[minmax(11rem,auto)] gap-3";

const MOSAIC_SPAN: Record<NotificationMosaicLayout, string> = {
  hero: "col-span-1 row-span-2 min-h-[11rem]",
  half: "col-span-1 row-span-2 min-h-[11rem]",
  cell: "col-span-1 min-h-[11rem]",
};

const MOSAIC_LAYOUT_SPRING: Transition = {
  type: "spring",
  stiffness: 420,
  damping: 40,
  mass: 0.8,
};

const MOSAIC_ENTER_TRANSITION: Transition = {
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1],
};

const INSTANT: Transition = { duration: 0 };

type MosaicBox = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Decorative skeleton pattern only — not used for real inbox rows. */
function skeletonMosaicLayout(index: number): NotificationMosaicLayout {
  if (index === 0) return "hero";
  if (index === 1 || index === 2) return "half";
  return "cell";
}

/**
 * Relative size tiers from loaded-list relevancy_score min/max.
 * Preserves input order; does not sort.
 * Near-tied scores (range below MIN_SCORE_RANGE) stay equal so tiny
 * absolute gaps like 0.004 vs 0.006 do not become hero vs cell.
 * Higher relevancy only grows row span (taller cards), never column span.
 */
const MIN_SCORE_RANGE = 0.05;

export function mosaicLayoutsFromRelevancyScores(
  scores: number[],
): NotificationMosaicLayout[] {
  if (scores.length === 0) return [];

  const normalized = scores.map((score) => Math.max(0, score ?? 0));
  let minScore = normalized[0];
  let maxScore = normalized[0];
  for (let i = 1; i < normalized.length; i++) {
    const score = normalized[i];
    if (score < minScore) minScore = score;
    if (score > maxScore) maxScore = score;
  }

  const range = maxScore - minScore;
  if (range < MIN_SCORE_RANGE) {
    return normalized.map(() => "cell");
  }

  return normalized.map((score) => {
    const span = (score - minScore) / range;
    if (span === 1) return "hero";
    if (span >= 0.5) return "half";
    return "cell";
  });
}

function SkeletonTile({
  span,
  delay,
}: {
  span: string;
  delay: number;
}) {
  return (
    <div
      className={cn("animate-pulse rounded-[1.5rem] bg-muted/50", span)}
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

export function NotificationObjectMosaicSkeleton() {
  return (
    <div className={MOSAIC_GRID_CLS}>
      {Array.from({ length: 9 }).map((_, index) => (
        <SkeletonTile
          key={index}
          span={MOSAIC_SPAN[skeletonMosaicLayout(index)]}
          delay={index * 80}
        />
      ))}
    </div>
  );
}

function MosaicTileFrame({
  shouldEnter,
  box,
  springGeometry,
  children,
}: {
  shouldEnter: boolean;
  box: MosaicBox;
  /** When false, snap geometry (mount, first measure, reduced motion). */
  springGeometry: boolean;
  children: ReactNode;
}) {
  // Freeze enter on first mount so cull remounts never replay fade/scale.
  const [playEnter] = useState(shouldEnter);
  // Enable springs only after a settled frame at the current box (no slide-from-0).
  const [canSpring, setCanSpring] = useState(false);

  useLayoutEffect(() => {
    if (!springGeometry) {
      setCanSpring(false);
      return;
    }
    const id = requestAnimationFrame(() => setCanSpring(true));
    return () => cancelAnimationFrame(id);
  }, [springGeometry]);

  const geometryTransition =
    springGeometry && canSpring ? MOSAIC_LAYOUT_SPRING : INSTANT;
  const enterTransition = playEnter ? MOSAIC_ENTER_TRANSITION : INSTANT;

  return (
    <motion.div
      className="absolute origin-center"
      // Always seed geometry so Framer never interpolates left/top from 0.
      initial={{
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        opacity: playEnter ? 0 : 1,
        scale: playEnter ? 0.98 : 1,
      }}
      animate={{
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        opacity: 1,
        scale: 1,
      }}
      transition={{
        top: geometryTransition,
        left: geometryTransition,
        width: geometryTransition,
        height: geometryTransition,
        opacity: enterTransition,
        scale: enterTransition,
      }}
    >
      {children}
    </motion.div>
  );
}

export function NotificationObjectMosaic({
  notifications,
  context,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onFetchNextPage,
  empty,
}: {
  notifications: Notification[];
  context: NotificationConfigContext;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onFetchNextPage: () => void;
  empty: ReactNode;
}) {
  usePostMountFlush();

  const reduceMotion = useReducedMotion();
  const knownIdsRef = useRef<Set<number>>(new Set());
  const seededRef = useRef(false);
  /** IDs that should play enter motion this commit only (not culled remounts). */
  const enteringIdsRef = useRef<Set<number>>(new Set());
  /** Skip springs until the grid has a real measured width (avoids 960→actual slide). */
  const measuredWidthRef = useRef(false);
  const [geometryReady, setGeometryReady] = useState(false);

  const columns = MOSAIC_COLUMNS;
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [viewport, setViewport] = useState({ top: 0, height: 0 });
  // Row unit grows on wide screens; the shell width may not change across the breakpoint.
  const isWide = useMediaQuery(MOSAIC_WIDE_QUERY);

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  useEffect(() => {
    if (!gridEl) return;
    const measure = () => {
      const width = gridEl.clientWidth;
      setGridWidth(width);
      if (width > 0 && !measuredWidthRef.current) {
        measuredWidthRef.current = true;
        // Defer springs one frame after first real measure so tiles snap in place.
        requestAnimationFrame(() => setGeometryReady(true));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(gridEl);
    return () => observer.disconnect();
  }, [gridEl]);

  useEffect(() => {
    if (!scrollRoot) return;
    const sync = () => {
      setViewport({
        top: scrollRoot.scrollTop,
        height: scrollRoot.clientHeight,
      });
    };
    sync();
    scrollRoot.addEventListener("scroll", sync, { passive: true });
    const observer = new ResizeObserver(sync);
    observer.observe(scrollRoot);
    return () => {
      scrollRoot.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [scrollRoot]);

  // Seed on first non-empty list (no enter). Later commits: brand-new IDs enter
  // this render only; layoutEffect then marks them known so the next render
  // (and cull remounts) no longer treat them as entering.
  if (!seededRef.current) {
    enteringIdsRef.current = new Set();
  } else {
    const entering = new Set<number>();
    for (const notification of notifications) {
      if (!knownIdsRef.current.has(notification.id)) {
        entering.add(notification.id);
      }
    }
    enteringIdsRef.current = entering;
  }

  useLayoutEffect(() => {
    if (notifications.length === 0) return;
    if (!seededRef.current) {
      knownIdsRef.current = new Set(notifications.map((n) => n.id));
      seededRef.current = true;
      return;
    }
    for (const notification of notifications) {
      knownIdsRef.current.add(notification.id);
    }
  }, [notifications]);

  const layouts = useMemo(
    () =>
      mosaicLayoutsFromRelevancyScores(
        notifications.map((n) => n.relevancy_score ?? 0),
      ),
    [notifications],
  );

  const { placements, rowCount } = useMemo(
    () => placeMosaicLayouts(layouts, columns),
    [layouts, columns],
  );

  const totalHeight = mosaicGridHeight(rowCount);

  // Never paint tiles at a guessed width — wait for the first real measure.
  const layoutWidth = gridWidth > 0 ? gridWidth : 0;
  const canPaintTiles = layoutWidth > 0;

  const visiblePlacements = useMemo(() => {
    if (!canPaintTiles) return [];
    const rowUnit = mosaicRowUnitPx();
    const overscan = rowUnit * 3;
    const viewTop = viewport.top - overscan;
    const viewBottom =
      viewport.top +
      Math.max(viewport.height, rowUnit * 4) +
      overscan;
    return placements.filter((placement) => {
      const box = mosaicPlacementStyle(placement, columns, layoutWidth);
      return box.top + box.height > viewTop && box.top < viewBottom;
    });
  }, [placements, columns, layoutWidth, viewport, canPaintTiles, isWide]);

  // If the packed top rows still have holes and more pages exist, keep
  // fetching so column-3 backfill does not wait on the user to scroll.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isLoading) return;
    if (placements.length === 0) return;

    const probeRows = 6;
    const filled = new Set<string>();
    for (const placement of placements) {
      for (let r = placement.row; r < placement.row + placement.rowSpan; r++) {
        if (r >= probeRows) continue;
        for (
          let c = placement.col;
          c < placement.col + placement.colSpan;
          c++
        ) {
          filled.add(`${r},${c}`);
        }
      }
    }

    let empty = 0;
    const maxRow = Math.min(
      probeRows,
      Math.max(rowCount, 1),
    );
    for (let r = 0; r < maxRow; r++) {
      for (let c = 0; c < columns; c++) {
        if (!filled.has(`${r},${c}`)) empty++;
      }
    }

    if (empty > 0) onFetchNextPage();
  }, [
    columns,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    onFetchNextPage,
    placements,
    rowCount,
  ]);

  const springGeometry = Boolean(geometryReady && !reduceMotion);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden"
      >
        {notifications.length === 0 && isLoading ? (
          <div className="px-5 pb-4 pt-3 sm:px-7">
            <NotificationObjectMosaicSkeleton />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col items-stretch px-5 pb-4 pt-3 sm:px-7">
            {empty}
          </div>
        ) : (
          <div className="px-5 pb-4 pt-3 sm:px-7">
            <InfiniteScroll
              isLoading={isFetchingNextPage}
              hasMore={!!hasNextPage}
              next={onFetchNextPage}
              root={scrollRoot}
              rootMargin="0px 0px 200px 0px"
            >
              <motion.div
                ref={setGridEl}
                className="relative w-full"
                initial={{ height: totalHeight }}
                animate={{ height: totalHeight }}
                transition={springGeometry ? MOSAIC_LAYOUT_SPRING : INSTANT}
              >
                {canPaintTiles
                  ? visiblePlacements.map((placement) => {
                      const notification = notifications[placement.index];
                      if (!notification) return null;
                      const layout = layouts[placement.index] ?? "cell";
                      const box = mosaicPlacementStyle(placement, columns, layoutWidth);
                      const shouldEnter =
                        !reduceMotion && enteringIdsRef.current.has(notification.id);
                      return (
                        <MosaicTileFrame
                          key={notification.id}
                          shouldEnter={shouldEnter}
                          box={box}
                          springGeometry={springGeometry}
                        >
                          <NotificationTile
                            notification={notification}
                            context={context}
                            layout={layout}
                            className="h-full min-h-0"
                          />
                        </MosaicTileFrame>
                      );
                    })
                  : null}
              </motion.div>
              {isFetchingNextPage ? (
                <div className="flex justify-center pb-1 pt-4">
                  <Loader2
                    className="size-4 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                </div>
              ) : (
                <div className="h-1" />
              )}
            </InfiniteScroll>
          </div>
        )}
      </div>
    </div>
  );
}
